import { useTranslation } from 'react-i18next';
import { SEO } from '../../components-new/SEO/SEO';
import { AnimatedSection } from '../../components-new/AnimatedSection/AnimatedSection';
import { useCookieConsent } from '../../contexts/CookieConsentContext/CookieConsentContext';
import './style.less';

export const PrivacyPolicy = () => {
  const { t } = useTranslation();
  const { resetConsent, hasConsent } = useCookieConsent();

  const dataItems = t('privacyPolicy.dataCollection.items', { returnObjects: true });
  const legalBasisItems = t('privacyPolicy.legalBasis.items', { returnObjects: true });
  const rightsItems = t('privacyPolicy.rights.items', { returnObjects: true });
  const thirdPartyItems = t('privacyPolicy.thirdParties.items', { returnObjects: true });
  const retentionItems = t('privacyPolicy.dataRetention.items', { returnObjects: true });

  return (
    <main className="privacy-policy-page">
      <SEO
        title={t('privacyPolicy.seo.title')}
        description={t('privacyPolicy.seo.description')}
        keywords={t('privacyPolicy.seo.keywords')}
      />

      {/* Hero Section */}
      <section className="section page-hero">
        <div className="container">
          <AnimatedSection>
            <h1>{t('privacyPolicy.title')}</h1>
            <p className="subtitle">{t('privacyPolicy.subtitle')}</p>
          </AnimatedSection>
        </div>
      </section>

      {/* Content Section */}
      <section className="section">
        <div className="container">
          <div className="privacy-content">
            {/* Introduction */}
            <AnimatedSection className="privacy-section">
              <h2>{t('privacyPolicy.intro.title')}</h2>
              <p>{t('privacyPolicy.intro.text')}</p>
            </AnimatedSection>

            {/* Data Collection */}
            <AnimatedSection className="privacy-section" delay={0.1}>
              <h2>{t('privacyPolicy.dataCollection.title')}</h2>
              <p>{t('privacyPolicy.dataCollection.text')}</p>
              <ul>
                {Array.isArray(dataItems) &&
                  dataItems.map((item, index) => <li key={index}>{item}</li>)}
              </ul>
            </AnimatedSection>

            {/* Legal Basis */}
            <AnimatedSection className="privacy-section" delay={0.15}>
              <h2>{t('privacyPolicy.legalBasis.title')}</h2>
              <p>{t('privacyPolicy.legalBasis.text')}</p>
              <ul>
                {Array.isArray(legalBasisItems) &&
                  legalBasisItems.map((item, index) => <li key={index}>{item}</li>)}
              </ul>
            </AnimatedSection>

            {/* Newsletter */}
            <AnimatedSection className="privacy-section" delay={0.2}>
              <h2>{t('privacyPolicy.newsletter.title')}</h2>
              <p>{t('privacyPolicy.newsletter.text')}</p>
              <p>{t('privacyPolicy.newsletter.unsubscribe')}</p>
            </AnimatedSection>

            {/* Cookies */}
            <AnimatedSection className="privacy-section" delay={0.25}>
              <h2>{t('privacyPolicy.cookies.title')}</h2>
              <p>{t('privacyPolicy.cookies.text')}</p>

              <div className="cookie-type">
                <h3>{t('privacyPolicy.cookies.necessary.title')}</h3>
                <p>{t('privacyPolicy.cookies.necessary.text')}</p>
              </div>

              <div className="cookie-type">
                <h3>{t('privacyPolicy.cookies.analytics.title')}</h3>
                <p>{t('privacyPolicy.cookies.analytics.text')}</p>
              </div>

              {/* Cookie Settings Button */}
              {hasConsent && (
                <div className="cookie-settings-action">
                  <button className="btn-secondary" onClick={resetConsent} type="button">
                    <i className="fas fa-cog" aria-hidden="true"></i>
                    {t('cookies.manage')}
                  </button>
                </div>
              )}
            </AnimatedSection>

            {/* Your Rights */}
            <AnimatedSection className="privacy-section" delay={0.35}>
              <h2>{t('privacyPolicy.rights.title')}</h2>
              <p>{t('privacyPolicy.rights.text')}</p>
              <ul>
                {Array.isArray(rightsItems) &&
                  rightsItems.map((item, index) => <li key={index}>{item}</li>)}
              </ul>
            </AnimatedSection>

            {/* Third Parties */}
            <AnimatedSection className="privacy-section" delay={0.4}>
              <h2>{t('privacyPolicy.thirdParties.title')}</h2>
              <p>{t('privacyPolicy.thirdParties.text')}</p>
              <ul>
                {Array.isArray(thirdPartyItems) &&
                  thirdPartyItems.map((item, index) => <li key={index}>{item}</li>)}
              </ul>
              <p>{t('privacyPolicy.thirdParties.note')}</p>
            </AnimatedSection>

            {/* Data Transfer */}
            <AnimatedSection className="privacy-section" delay={0.45}>
              <h2>{t('privacyPolicy.dataTransfer.title')}</h2>
              <p>{t('privacyPolicy.dataTransfer.text')}</p>
            </AnimatedSection>

            {/* Data Retention */}
            <AnimatedSection className="privacy-section" delay={0.5}>
              <h2>{t('privacyPolicy.dataRetention.title')}</h2>
              <p>{t('privacyPolicy.dataRetention.text')}</p>
              <ul>
                {Array.isArray(retentionItems) &&
                  retentionItems.map((item, index) => <li key={index}>{item}</li>)}
              </ul>
            </AnimatedSection>

            {/* Right to Complain */}
            <AnimatedSection className="privacy-section" delay={0.55}>
              <h2>{t('privacyPolicy.complaint.title')}</h2>
              <p>{t('privacyPolicy.complaint.text')}</p>
              <div className="contact-info">
                <p><strong>{t('privacyPolicy.complaint.authority')}</strong></p>
                <p>{t('privacyPolicy.complaint.address')}</p>
                <p>{t('privacyPolicy.complaint.website')}</p>
                <p>{t('privacyPolicy.complaint.authorityEmail')}</p>
              </div>
            </AnimatedSection>

            {/* Automated Decisions */}
            <AnimatedSection className="privacy-section" delay={0.6}>
              <h2>{t('privacyPolicy.automatedDecisions.title')}</h2>
              <p>{t('privacyPolicy.automatedDecisions.text')}</p>
            </AnimatedSection>

            {/* Contact */}
            <AnimatedSection className="privacy-section" delay={0.65}>
              <h2>{t('privacyPolicy.contact.title')}</h2>
              <p>{t('privacyPolicy.contact.text')}</p>
              <div className="contact-info">
                <p>
                  <i className="fas fa-envelope" aria-hidden="true"></i>
                  <a href="mailto:stefani.georgieva@interior-sg.com">stefani.georgieva@interior-sg.com</a>
                </p>
                <p>
                  <i className="fas fa-phone" aria-hidden="true"></i>
                  <a href="tel:+359886275939">+359 88 627 5939</a>
                </p>
              </div>
            </AnimatedSection>

            {/* Changes */}
            <AnimatedSection className="privacy-section" delay={0.7}>
              <h2>{t('privacyPolicy.changes.title')}</h2>
              <p>{t('privacyPolicy.changes.text')}</p>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </main>
  );
};
